/* eslint-disable no-undef */
import { v4 as uuidv4 } from 'uuid';
import { CONFIG } from './config';

// Helper to get value from config (tokens) or env (local dev)
const getConfigValue = (configValue, envValue, fallback) => {
  // In deployed environments, the pipeline replaces token placeholders with real values.
  // If configValue has been replaced by the pipeline it will be a plain value — use it.
  //
  // IMPORTANT: charCodeAt is used instead of startsWith/endsWith with literal delimiter
  // strings because the replacetokens pipeline task scans the built JS bundle and would
  // match and corrupt those string literals. charCodeAt calls cannot be matched.
  // charCodeAt values used: '#'=35, '{'=123, '}'=125
  const isUnreplacedToken =
    typeof configValue === 'string' &&
    configValue.length >= 4 &&
    configValue.charCodeAt(0) === 35 &&
    configValue.charCodeAt(1) === 123 &&
    configValue.charCodeAt(configValue.length - 2) === 125 &&
    configValue.charCodeAt(configValue.length - 1) === 35;

  if (configValue && !isUnreplacedToken) return configValue;

  // configValue is still a placeholder (local dev) — fall back to import.meta.env (.env.local)
  const isValidEnvValue = envValue && typeof envValue === 'string' && envValue.trim() !== '';
  if (isValidEnvValue) return envValue;

  // Last resort fallback
  return fallback;
};

// Debug: Expose env vars globally for inspection
// if (typeof window !== 'undefined') {
//   window.__DEBUG_ENV__ = {
//     importMetaEnv: import.meta.env,
//     config: CONFIG,
//     allViteVars: Object.keys(import.meta.env)
//       .filter((key) => key.startsWith('VITE_'))
//       .reduce((acc, key) => {
//         acc[key] = import.meta.env[key];
//         return acc;
//       }, {}),
//   };
// }

export default {
  getCashSupportEmail() {
    return getConfigValue(CONFIG.cashSupportEmail, import.meta.env.VITE_CASH_SUPPORT_EMAIL, '[TBD]');
  },

  getCustomerServicePhone() {
    return getConfigValue(CONFIG.customerServicePhone, import.meta.env.VITE_CUSTOMER_SERVICE_PHONE, '1300 365 096');
  },

  getAdminRoleId() {
    return getConfigValue(
      CONFIG.adminRoleId,
      import.meta.env.VITE_ADMIN_ROLE_ID,
      '80932a67-11cf-4ba1-a7bc-06fa3243d974'
    );
  },

  isAdminPortal() {
    const val = getConfigValue(CONFIG.isAdminPortal, import.meta.env.VITE_IS_ADMIN_PORTAL, 'false');
    return val != null ? val.toLowerCase() === 'true' : false;
  },

  getPaymentRegisterCardUrl() {
    return getConfigValue(
      CONFIG.payment.registerCardUrl,
      import.meta.env.VITE_PAYMENT_REGISTER_CARD_URL,
      'https://pay.ampol.com.au/home/registercard'
    );
  },

  getPaymentRegisterDDAuthUrl() {
    return getConfigValue(
      CONFIG.payment.registerDDAuthUrl,
      import.meta.env.VITE_PAYMENT_REGISTER_DD_AUTH_URL,
      'https://www.ampol.com.au/-/media/files/ampol-au/fuel-cards/ampol-direct-debit-authorisation-form-v3.ashx'
    );
  },

  getPaymentCardPaymentUrl() {
    return getConfigValue(
      CONFIG.payment.cardPaymentUrl,
      import.meta.env.VITE_PAYMENT_CARD_PAYMENT_URL,
      'https://pay.ampol.com.au/home/submitpayment'
    );
  },

  getMockSwitch() {
    return getConfigValue(CONFIG.mockEnabled, import.meta.env.VITE_MOCK_ENABLED, 'on');
  },

  getBaseUrl() {
    return getConfigValue(CONFIG.baseUrl, import.meta.env.VITE_BASE_URL, 'http://localhost:3000/');
  },

  getApiBaseUrl() {
    return getConfigValue(
      CONFIG.apiBaseUrl,
      import.meta.env.VITE_API_BASE_URL,
      'https://dig-api-stg.nonprod.ampol.com.au/'
    );
  },

  getApimBaseUrl() {
    return getConfigValue(
      CONFIG.apimBaseUrl,
      import.meta.env.VITE_APIM_BASE_URL,
      'https://dig-api-stg.nonprod.ampol.com.au/'
    );
  },

  getApimKey() {
    return getConfigValue(CONFIG.apimKey, import.meta.env.VITE_APIM_KEY, '1a002da614fb4332b80017b376d779b4');
  },

  getB2COptions() {
    return {
      instance: getConfigValue(
        CONFIG.b2c.instance,
        import.meta.env.VITE_B2C_INSTANCE,
        'https://id.nonprod.ampol.com.au/'
      ),
      tenant: getConfigValue(CONFIG.b2c.tenant, import.meta.env.VITE_B2C_TENANT, 'id.nonprod.ampol.com.au'),
      signInPolicy: getConfigValue(
        CONFIG.b2c.signInPolicy,
        import.meta.env.VITE_B2C_SIGN_IN_POLICY,
        'b2c_1a_v2_connect_sign_up_sign_in'
      ),
      resetPasswordPolicy: getConfigValue(
        CONFIG.b2c.resetPolicy,
        import.meta.env.VITE_B2C_RESET_POLICY,
        'B2C_1A_V2_LOCAL_ACCOUNT_PASSWORD_CHANGE'
      ),
      forgotPasswordPolicy: getConfigValue(
        CONFIG.b2c.forgotPasswordPolicy,
        import.meta.env.VITE_B2C_FORGOT_PASSWORD_POLICY,
        'B2C_1A_MyAmpolCard_Dev_ForgotPassword'
      ),
      appId: getConfigValue(CONFIG.b2c.appId, import.meta.env.VITE_B2C_APP_ID, 'c0ec4786-70a1-4c5b-ad2d-d5a9a59b7aa3'),
      scopes: [
        CONFIG.b2c.scopes ||
          import.meta.env.VITE_B2C_SCOPES ||
          'https://caltexb2ctest01.onmicrosoft.com/api/user_impersonation',
      ],
    };
  },

  getResetPasswordUrl() {
    //TODO: Hardcoded code_challenge is a workaround of reset policy redirection which is not yet built in the MSAL
    //https://github.com/MicrosoftDocs/azure-docs/issues/68299

    let b2cOptions = this.getB2COptions();
    let scopes = `${b2cOptions.appId} openid profile offline_access`;
    let resetPasswordUrl =
      `${b2cOptions.instance}${b2cOptions.tenant}/${b2cOptions.resetPasswordPolicy}/oauth2/v2.0/authorize?response_type=code&` +
      `scope=${encodeURIComponent(scopes)}&client_id=${b2cOptions.appId}&redirect_uri=${encodeURIComponent(this.getBaseUrl())}&` +
      `state=${uuidv4()}&nonce=${uuidv4()}&client_info=1&x-client-SKU=MSAL.JS&x-client-Ver=2.14.0&client-request-id=${uuidv4()}&response_mode=fragment` +
      '&code_challenge=YTFjNjI1OWYzMzA3MTI4ZDY2Njg5M2RkNmVjNDE5YmEyZGRhOGYyM2IzNjdmZWFhMTQ1ODg3NDcxY2Nl';
    return resetPasswordUrl;
  },

  getForgotPasswordPolicyUrl() {
    let b2cOptions = this.getB2COptions();
    //The Azure B2C Redirects with the error code AADB2C90118 for application to handle the forgot password flow
    let policyUrl = `${b2cOptions.instance}tfp/${b2cOptions.tenant}/${b2cOptions.forgotPasswordPolicy}`;
    return policyUrl;
  },

  getLoginPolicyUrl() {
    //Default the Policy to show the Sign In user flow on the Application startup
    let b2cOptions = this.getB2COptions();
    let policyUrl = `${b2cOptions.instance}tfp/${b2cOptions.tenant}/${b2cOptions.signInPolicy}`;
    return policyUrl;
  },

  getKnownAuthority() {
    //This is a domain of <tenant>.b2clogin.com so that its omitted from the validation
    let b2cOptions = this.getB2COptions();
    let domain = new URL(b2cOptions.instance);
    return domain.hostname;
  },

  isAmpolCashEnabled() {
    const val = getConfigValue(CONFIG.isAmpolCashEnabled, import.meta.env.VITE_IS_AMPOL_CASH_ENABLED, 'true');
    return val != null ? val.toLowerCase() === 'true' : false;
  },

  getNotificationIntervalMinutes() {
    return getConfigValue(CONFIG.notificationIntervalMinutes, import.meta.env.VITE_NOTIFICATION_INTERVAL_MINUTES, 15);
  },

  getApplicationInsightsConnection() {
    return getConfigValue(
      CONFIG.applicationInsights,
      import.meta.env.VITE_APPLICATION_INSIGHTS,
      'TEST_CONNECTION_STRING'
    );
  },

  getIsNewCardModalsEnabled() {
    return getConfigValue(CONFIG.enableNewCardModals, import.meta.env.VITE_ENABLE_NEW_CARD_MODALS, 'false');
  },

  getIsZonkaEnabled() {
    const val = getConfigValue(CONFIG.zonkaEnabled, import.meta.env.VITE_ZONKA_ENABLED, 'false');
    return val != null ? val.toLowerCase() === 'true' : false;
  },

  getIsEvWay4Enabled() {
    const val = getConfigValue(CONFIG.evWay4Enabled, import.meta.env.VITE_EV_WAY4, 'false');
    return val != null ? val.toLowerCase() === 'true' : false;
  },

  getIsCardLimitsReadOnly() {
    const val = getConfigValue(CONFIG.cardLimitsReadOnly, import.meta.env.VITE_CARD_LIMITS_READ_ONLY, 'true');
    // Preserve the existing flag as the switch for daily credit-limit filtering.
    return val == null || val.toLowerCase() !== 'false';
  },
};
